import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BoardResponse, BoardColumn } from '../services/api/board';
import { deleteTask } from '../services/api/board';

export interface DeleteTaskInput {
  projectId: string;
  taskId: number;
}

interface MutationContext {
  previous: BoardResponse | undefined;
}

/**
 * Phase 24 Plan 24-08 — internal task delete. Optimistically removes the
 * task row by id; rolls back on error. Server pre-delete audit row is
 * captured by the backend before the row is removed (Plan 24-07).
 *
 * Modal-close side-effect is the caller's responsibility — pass a per-mutate
 * { onSuccess: () => closeModal() } option from KanbanBoardPage.
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation<{ deleted: boolean; taskId: number }, Error, DeleteTaskInput, MutationContext>({
    mutationFn: (input) => deleteTask(input.taskId),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ['board', input.projectId, 'internal'] });
      const previous = queryClient.getQueryData<BoardResponse>([
        'board',
        input.projectId,
        'internal',
      ]);
      if (previous) {
        queryClient.setQueryData<BoardResponse>(
          ['board', input.projectId, 'internal'],
          applyOptimisticDelete(previous, input.taskId),
        );
      }
      return { previous };
    },
    onError: (_err, vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['board', vars.projectId, 'internal'], context.previous);
      }
    },
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({
        queryKey: ['board', input.projectId, 'internal'],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Pure helper — exported for unit testing.
// ---------------------------------------------------------------------------

export function applyOptimisticDelete(board: BoardResponse, taskId: number): BoardResponse {
  const next: BoardColumn[] = board.columns.map((col) => ({
    ...col,
    tasks: col.tasks.filter((t) => t.id !== taskId),
  }));
  return { columns: next };
}
